import { useLocation } from 'react-router-dom'
import { CalendarIcon, HomeIcon, PackageIcon, UserIcon } from 'lucide-react'
import type { IconComponent } from '../../ui/icon'
import { useFeatures } from '../../hooks/useFeatures'

export type AccountPage = {
  to: string
  /** Menu, duimbalk en kruimelpad. */
  short: string
  /** Paginakop. */
  title: string
  icon: IconComponent
  end?: boolean
}

type SubPage = { to: string; short: string; title: string; parent: string; feature?: 'diary' }

export const ACCOUNT_HOME = '/account'
export const ACCOUNT_SALON = '/account/afspraken'
export const ACCOUNT_THUIS = '/account/thuis'
export const ACCOUNT_PROFILE = '/account/gegevens'
/** Oude plek van het abonnement (links in mails): stuurt door naar Thuis › Levering. */
export const DELIVERY_HREF = `${ACCOUNT_THUIS}#levering`

/**
 * Vier tabs, voor elke klant dezelfde (docs/VORMGEVING.md §6): Overzicht · Salon · Thuis · Ik.
 * Dezelfde namen in zijmenu, duimbalk, kruimelpad en paginakop.
 */
export const accountTabs: AccountPage[] = [
  { to: ACCOUNT_HOME, short: 'Overzicht', title: 'Overzicht', icon: HomeIcon, end: true },
  { to: ACCOUNT_SALON, short: 'Salon', title: 'Salon', icon: CalendarIcon },
  { to: ACCOUNT_THUIS, short: 'Thuis', title: 'Thuis', icon: PackageIcon },
  { to: ACCOUNT_PROFILE, short: 'Ik', title: 'Mijn gegevens', icon: UserIcon },
]

/** Pagina's zonder eigen tab: ze horen bij een tab, die dan actief blijft. */
const subPages: SubPage[] = [
  { to: '/account/bestellingen', short: 'Bestellingen', title: 'Bestellingen en facturen', parent: ACCOUNT_THUIS },
  { to: '/account/dagboek', short: 'Dagboek', title: 'Fotodagboek', parent: ACCOUNT_THUIS, feature: 'diary' },
]

/** Het menu van Mijn omgeving en waar de klant nu is. */
export function useAccountNav() {
  const features = useFeatures()
  const { pathname } = useLocation()
  const path = pathname.replace(/\/+$/, '') || ACCOUNT_HOME

  const tab = accountTabs.find((t) => t.to === path)
  const sub = subPages.find((p) => p.to === path && (p.feature !== 'diary' || features.diary))
  const parent = sub ? accountTabs.find((t) => t.to === sub.parent) : undefined
  const current = tab ?? (sub ? { to: sub.to, short: sub.short, title: sub.title } : undefined)
  /** De tab die oplicht: op een subpagina die van de ouder. */
  const activeTab = tab?.to ?? parent?.to ?? null

  /**
   * Kruimelpad boven de paginakop, alleen op een subpagina ("Thuis › Bestellingen"): op een tab
   * zegt het menu al waar je bent, en een pad terug naar dezelfde plek is ruis.
   */
  const trail: { to: string; label: string }[] = parent ? [{ to: parent.to, label: parent.short }] : []

  return { tabs: accountTabs, current, trail, activeTab }
}
